import { FilterPeriodState, FilterPeriodType } from '@shared/components/filter-period';
import { GroupedOption } from '@shared/components/group-select';
import { Option } from '@shared/components/select-new/Select';
import { SwitchType } from '@shared/components/switch';
import { LocationType, WipFeesType } from '@shared/types/common/submarket';
import { IndexResponse } from '@shared/types/services';
import { QueryFactory } from '@shared/utils/http-client';

import { FilterAutocompleteItemState } from './autocomplete';
import { Range } from './range';

export enum FilterType {
  date,
  dateRange,
  locationRadios,
  locationRegion,
  lookupAgency,
  lookupAgent,
  lookupCompany,
  lookupContact,
  lookupUser,
  multiGroupSelect,
  multiLookupAgent,
  multiLookupList,
  multiLookupCompany,
  multiLookupDisposal,
  multiSelect,
  numberFieldWithSingleSelect,
  range,
  requirementDate,
  singleGroupSelect,
  singleSelect,
  switch,
  text,
  textFieldWithSingleSelect,
  monthQuarterYearRange,
  multiLookupUnifiedDisposal,
}

export type FiltersTypeConfig<TFT = FilterType> = {
  label: string;
  labelCheckbox?: boolean;
  placeholder?: string;
  removable?: boolean;
  tooltip?: string;
  type: TFT;
  visible?: boolean;
};

interface FieldWithSelectFilterTypeConfig<TF extends FilterType, TO extends Option = Option>
  extends FiltersTypeConfig<TF> {
  fieldLabel?: string;
  options: TO[];
  selectLabel?: string;
}

interface SelectFilterTypeConfig<TF extends FilterType, TO extends Option = Option> extends FiltersTypeConfig<TF> {
  options: TO[];
}

interface DateFilterTypeConfig extends FiltersTypeConfig<FilterType.date> {
  supportedTypes?: FilterPeriodType[];
}

interface LocationRadiosFilterTypeConfig extends FiltersTypeConfig<FilterType.locationRadios> {
  defaultMetric?: Metric;
}

interface MultiGroupSelectFilterTypeConfig extends FiltersTypeConfig<FilterType.multiGroupSelect> {
  groupOptions: GroupedOption[];
}

interface RangeFilterTypeConfig extends FiltersTypeConfig<FilterType.range> {
  currency?: boolean;
  currencyOptions?: Option[];
  defaultCurrency?: string;
  defaultMetric?: Metric;
  fromPlaceholder?: string;
  metric?: boolean;
  metricOptions?: Option[];
  suffix?: string;
  toPlaceholder?: string;
}

interface SingleGroupSelectFilterTypeConfig extends FiltersTypeConfig<FilterType.singleGroupSelect> {
  groupOptions: GroupedOption[];
}

interface TextFilterTypeConfig extends FiltersTypeConfig<FilterType.text> {
  suffix?: string;
}

interface MonthQuarterYearRangeFilterTypeConfig extends FiltersTypeConfig<FilterType.monthQuarterYearRange> {
  options: Option[];
}

interface SwitchFilterTypeConfig extends FiltersTypeConfig<FilterType.switch> {
  switchType: SwitchType;
  condition?: boolean;
  subLabel?: string;
}

interface MultiLookupListFilterTypeConfig extends FiltersTypeConfig<FilterType.multiLookupList> {
  queryFactory: QueryFactory<IndexResponse<ItemWithId>>;
}

interface LookupAgentFilterTypeConfig extends FiltersTypeConfig<FilterType.lookupAgent> {
  queryFactory?: QueryFactory<IndexResponse<ItemWithId>>;
}

export type FiltersTypeConfigs =
  | DateFilterTypeConfig
  | FieldWithSelectFilterTypeConfig<FilterType.numberFieldWithSingleSelect>
  | FieldWithSelectFilterTypeConfig<FilterType.textFieldWithSingleSelect>
  | FiltersTypeConfig<FilterType.dateRange>
  | FiltersTypeConfig<FilterType.locationRegion>
  | FiltersTypeConfig<FilterType.lookupAgency>
  | LookupAgentFilterTypeConfig
  | FiltersTypeConfig<FilterType.lookupCompany>
  | FiltersTypeConfig<FilterType.lookupContact>
  | FiltersTypeConfig<FilterType.multiLookupAgent>
  | FiltersTypeConfig<FilterType.multiLookupCompany>
  | FiltersTypeConfig<FilterType.multiLookupDisposal>
  | FiltersTypeConfig<FilterType.multiLookupUnifiedDisposal>
  | FiltersTypeConfig<FilterType.lookupUser>
  | FiltersTypeConfig<FilterType.requirementDate>
  | TextFilterTypeConfig
  | LocationRadiosFilterTypeConfig
  | MonthQuarterYearRangeFilterTypeConfig
  | MultiGroupSelectFilterTypeConfig
  | MultiLookupListFilterTypeConfig
  | RangeFilterTypeConfig
  | SingleGroupSelectFilterTypeConfig
  | SelectFilterTypeConfig<FilterType.multiSelect>
  | SelectFilterTypeConfig<FilterType.singleSelect>
  | SwitchFilterTypeConfig;

/**
 * PERSISTED BY NUMERIC VALUE — handle with care.
 *
 * This is a numeric (auto-incremented) enum. `usePinnedFilters` writes each
 * member's NUMBER (not its name) to localStorage, keyed per page + user, for filter drawers.
 * A member's position therefore IS its stored identity — changing positions silently reassigns users' pins.
 *
 * Rules:
 *   - ADD a key: append at the END only. Never insert at the top/middle — that shifts every later member by +1.
 *   - REMOVE a key: do NOT delete in place (it shifts every later member by -1). Leave the slot occupied.
 *   - RENAME a key: safe — the numeric value is unchanged. Update all consumers.
 *   - REORDER: never. Alphabetical/cosmetic ordering is not worth scrambling persisted pins. Keep insertion order stable.
 */
export enum AvailableFilters {
  advisoryStatus,
  advisoryType,
  agency,
  agent,
  amlOrKycCompleted,
  assignee,
  availableSpace,
  buildingGrade,
  buildingTypes,
  buildStatus,
  builtYear,
  bulkJobStatus,
  capVal,
  country,
  company,
  companySource,
  companyStatus,
  companyTags,
  companyType,
  completionDate,
  constructionStartYear,
  contact,
  contactStatus,
  contactTags,
  contactType,
  created,
  createdCrmCompanies,
  createdCrmContacts,
  createdDateRange,
  createdTransactionsSales,
  dateAdded,
  disposalBusinessRateMultiplier,
  disposalDevelopmentCompletionYear,
  disposalDevelopmentFloorSize,
  disposalDevelopmentFunding,
  disposalDevelopmentLikelihood,
  disposalDevelopmentNoOfFloors,
  disposalDevelopmentPreLetCommitted,
  disposalDevelopmentSpeculative,
  disposalDevelopmentStatus,
  disposalDevelopmentTotalAreaProposed,
  disposalDevelopmentType,
  disposalFittedSpaces,
  disposalHasPresentation,
  leaseTypes,
  disposalPublishedTo,
  disposalSaleType,
  disposalStatus,
  disposalSharedWithJointAgents,
  disposalSharedWithLandlord,
  disposalTermsOfEngagement,
  disposalTags,
  disposalWipStatus,
  dueDate,
  externalOnly,
  emptyPropertyRecords,
  feeBasis,
  feeSplitTeams,
  feesStage,
  feesStatus,
  feesType,
  fitoutConcept,
  fitoutCondition,
  floorsAboveGround,
  floorsBelowGround,
  floorUnitOptions,
  gdprConsent,
  has_active_disposals,
  has_active_leases,
  has_active_requirements,
  has_active_sales,
  has_active_viewings,
  has_investor_profile,
  hasAvailability,
  hasLeases,
  hasOpenTasks,
  hasSales,
  inResearchStock,
  independentBuilding,
  internalConfidentialityDate,
  investmentGrade,
  investmentMarketStatus,
  investorAmountUnderManagement,
  investorNationality,
  investorRanking,
  investorRegion,
  investorType,
  invoice,
  jointExclusiveInstructions,
  kycCompleted,
  leadAgent,
  leadTeam,
  leaseActivityStatus,
  leaseEvent,
  leaseBreak,
  leaseEnd,
  leaseExpiry,
  leaseReminder,
  leaseStart,
  lettingTypes,
  lettingTransactionTags,
  location,
  locationRadios,
  lotSize,
  marketing_lists,
  marketing_permission,
  nationality,
  newMatches,
  niy,
  occupiedSpace,
  office,
  ourInstructions,
  ownershipTypes,
  parkingSpacesAboveGround,
  parkingSpacesBelowGround,
  pendingMatches,
  planningStatus,
  postcode,
  probabilityRange,
  propertyAddOnFactor,
  propertySize,
  publishedToAtlas,
  publishedToRadius,
  recordOwner,
  refurbYear,
  rentCurrency,
  rentReview,
  requirementType,
  internalConfidentiality,
  reverseSegmentIntersectLogic,
  riskProfile,
  salePrice,
  saleType,
  saleTransactionTags,
  sectors,
  shareWithWip,
  showSingleFloors,
  size,
  societyAgent,
  societyBuildingTypes,
  societyDisposalAdvertStatus,
  societyDisposalLeaseTypes,
  sources,
  startDate,
  tags,
  teams,
  tenancyStatus,
  tenure,
  tny,
  transactionDate,
  transactionStatus,
  typicalFloorSize,
  updated,
  user,
  vacantSpace,
  waultToExpiry,
  withoutDisposalTags,
  rent,
  publishedToSociety,
  fittedSpaces,
  knownTenant,
  price,
  showPendingMatch,
  requirementStatus,
  feeSource,
  feeSourceDetailOwnActivity,
  feeSourceDetailCrossSellInternal,
  feeSourceDetailCrossSellExternal,
  societyAcquisitionAdvertStatus,
  societyAcquisitionTenure,
  acquisitionAdvertType,
  acquisitionIntelStatus,
  targetOccupancyYear,
  targetOccupancyMonth,
  requirementReason,
  qualityOfRequirement,
  dateUpdatedRange,
  requirementLists,
  investmentMinimumNetYield,
  investmentInternalRateOfReturn,
  targetCloseDate,
  targetOccupancy,
  disposalMatches,
}

export type AvailableFiltersHash = { [key: number]: AvailableFilters };

export interface FieldWithSingleSelectState {
  field: string;
  select: Id;
}

export interface FilterLocationFieldState {
  [LocationType.postcode]?: string;
  [LocationType.submarkets]?: string[];
  [LocationType.teamSubmarkets]?: string[];
}

export interface FilterLocationRegionFieldState {
  [WipFeesType.city]?: string;
  [WipFeesType.country]?: string;
}

export interface FilterRangeState extends Range {
  metric?: Metric;
  currency?: string;
}

export interface FilterRequirementDateFieldState {
  [WipFeesType.due_date]?: Range;
  [WipFeesType.start_date]?: Range;
}

export interface FilterMonthQuarterYearRangeState {
  month_from: string;
  month_to: string;
  year_from: string;
  year_to: string;
}

interface FilterStateValue<T = any> {
  checkboxValue?: boolean;
  value: T;
}

export interface UnifiedDisposalLookupValue {
  address?: string;
  id: string;
  name: string;
}

export type FilterStateValues = {
  [FilterType.date]: FilterStateValue<FilterPeriodState>;
  [FilterType.dateRange]: FilterStateValue<Range>;
  [FilterType.locationRadios]: FilterStateValue<FilterLocationFieldState>;
  [FilterType.locationRegion]: FilterStateValue<FilterLocationRegionFieldState>;
  [FilterType.lookupAgency]: FilterStateValue<FilterAutocompleteItemState>;
  [FilterType.lookupAgent]: FilterStateValue<FilterAutocompleteItemState>;
  [FilterType.lookupCompany]: FilterStateValue<FilterAutocompleteItemState>;
  [FilterType.lookupContact]: FilterStateValue<FilterAutocompleteItemState>;
  [FilterType.multiLookupAgent]: FilterStateValue<ItemWithId[]>;
  [FilterType.multiLookupList]: FilterStateValue<ItemWithId[]>;
  [FilterType.multiLookupCompany]: FilterStateValue<ItemWithId[]>;
  [FilterType.multiLookupDisposal]: FilterStateValue<ItemWithId[]>;
  [FilterType.multiLookupUnifiedDisposal]: FilterStateValue<UnifiedDisposalLookupValue[]>;
  [FilterType.lookupUser]: FilterStateValue<FilterAutocompleteItemState>;
  [FilterType.multiGroupSelect]: FilterStateValue<Id[]>;
  [FilterType.multiSelect]: FilterStateValue<Id[]>;
  [FilterType.numberFieldWithSingleSelect]: FilterStateValue<FieldWithSingleSelectState>;
  [FilterType.range]: FilterStateValue<FilterRangeState>;
  [FilterType.requirementDate]: FilterStateValue<FilterRequirementDateFieldState>;
  [FilterType.singleGroupSelect]: FilterStateValue<Id | undefined>;
  [FilterType.singleSelect]: FilterStateValue<Id | undefined>;
  [FilterType.switch]: FilterStateValue<boolean | undefined>;
  [FilterType.text]: FilterStateValue<string | undefined>;
  [FilterType.textFieldWithSingleSelect]: FilterStateValue<FieldWithSingleSelectState>;
  [FilterType.monthQuarterYearRange]: FilterStateValue<FilterMonthQuarterYearRangeState>;
};

export type AllFilterStateValues = ValueOf<FilterStateValues>;

type PartialFilterStateValue<T extends AllFilterStateValues = AllFilterStateValues> = FilterStateValue<
  Partial<T['value']>
>;

export type PartialFilterStateValues = {
  [FilterType.date]: PartialFilterStateValue<FilterStateValues[FilterType.date]>;
  [FilterType.dateRange]: PartialFilterStateValue<FilterStateValues[FilterType.dateRange]>;
  [FilterType.locationRadios]: PartialFilterStateValue<FilterStateValues[FilterType.locationRadios]>;
  [FilterType.locationRegion]: PartialFilterStateValue<FilterStateValues[FilterType.locationRegion]>;
  [FilterType.lookupAgency]: PartialFilterStateValue<FilterStateValues[FilterType.lookupCompany]>;
  [FilterType.lookupAgent]: PartialFilterStateValue<FilterStateValues[FilterType.lookupAgent]>;
  [FilterType.lookupAgent]: PartialFilterStateValue<FilterStateValues[FilterType.lookupCompany]>;
  [FilterType.lookupCompany]: PartialFilterStateValue<FilterStateValues[FilterType.lookupCompany]>;
  [FilterType.lookupContact]: PartialFilterStateValue<FilterStateValues[FilterType.lookupContact]>;
  [FilterType.lookupUser]: PartialFilterStateValue<FilterStateValues[FilterType.lookupUser]>;
  [FilterType.multiGroupSelect]: FilterStateValue<FilterStateValues[FilterType.multiGroupSelect]['value']>;
  [FilterType.multiLookupAgent]: PartialFilterStateValue<FilterStateValues[FilterType.multiLookupAgent]>;
  [FilterType.multiLookupList]: PartialFilterStateValue<FilterStateValues[FilterType.multiLookupList]>;
  [FilterType.multiLookupCompany]: PartialFilterStateValue<FilterStateValues[FilterType.multiLookupCompany]>;
  [FilterType.multiLookupDisposal]: PartialFilterStateValue<FilterStateValues[FilterType.multiLookupDisposal]>;
  [FilterType.multiLookupUnifiedDisposal]: PartialFilterStateValue<
    FilterStateValues[FilterType.multiLookupUnifiedDisposal]
  >;
  [FilterType.multiSelect]: FilterStateValue<FilterStateValues[FilterType.multiSelect]['value']>;
  [FilterType.numberFieldWithSingleSelect]: PartialFilterStateValue<
    FilterStateValues[FilterType.numberFieldWithSingleSelect]
  >;
  [FilterType.range]: PartialFilterStateValue<FilterStateValues[FilterType.range]>;
  [FilterType.requirementDate]: PartialFilterStateValue<FilterStateValues[FilterType.requirementDate]>;
  [FilterType.singleGroupSelect]: FilterStateValue<FilterStateValues[FilterType.singleGroupSelect]['value']>;
  [FilterType.singleSelect]: PartialFilterStateValue<FilterStateValues[FilterType.singleSelect]>;
  [FilterType.switch]: PartialFilterStateValue<FilterStateValues[FilterType.switch]>;
  [FilterType.text]: PartialFilterStateValue<FilterStateValues[FilterType.text]>;
  [FilterType.textFieldWithSingleSelect]: PartialFilterStateValue<
    FilterStateValues[FilterType.textFieldWithSingleSelect]
  >;
  [FilterType.monthQuarterYearRange]: PartialFilterStateValue<FilterStateValues[FilterType.monthQuarterYearRange]>;
};

// -------------------------------------------------------------------------------------------------
// Config-driven serialisation
//
// A FilterParamConfig describes how a single filter maps between its state value and the query
// params, keyed by FilterType. serialiseFilterState / deserialiseFilterState (shared/utils/filter)
// drive the defaults; the optional fields below cover the common deviations, and the serialise /
// deserialise escape hatches handle anything genuinely bespoke. A drawer supplies one config map; the
// engine is shared.
// -------------------------------------------------------------------------------------------------

// A multi-param key can be a base string (sub-keys derived by the type's convention, e.g. `${base}_from`),
// or an object that overrides specific sub-keys (and/or supplies its own `base` for the rest).
export type DerivableParamKey<TSubKey extends string> = string | ({ base?: string } & Partial<Record<TSubKey, string>>);

// Base shared by every config — keyed by FilterType, with the value type and param-key shape per type.
// serialise/deserialise are escape hatches for anything the type's default + flags can't express.
export interface BaseFilterParamConfig<TFilterType extends FilterType, TValue, TParam = string> {
  type: TFilterType;
  param: TParam;
  serialise?: (value: TValue | undefined) => Query<string | undefined>;
  deserialise?: (params?: Query) => TValue;
}

export interface MultiSelectFilterParamConfig
  extends BaseFilterParamConfig<FilterType.multiSelect | FilterType.multiGroupSelect, Id[]> {
  // Parse deserialised ids to numbers, for fields whose options use numeric ids — so the state matches the
  // option id type for selection, label display, and change-detection (URL params are always strings).
  numericIds?: boolean;
  // Value to deserialise to when the param is absent (e.g. a default status set).
  defaultValue?: Id[];
}

export type SingleSelectFilterParamConfig = BaseFilterParamConfig<FilterType.singleSelect, Id | undefined>;

export type SwitchFilterParamConfig = BaseFilterParamConfig<FilterType.switch, boolean | undefined>;

export type TextFilterParamConfig = BaseFilterParamConfig<FilterType.text, string>;

export type MultiLookupFilterParamConfig = BaseFilterParamConfig<
  | FilterType.multiLookupAgent
  | FilterType.multiLookupCompany
  | FilterType.multiLookupDisposal
  | FilterType.multiLookupList
  | FilterType.multiLookupUnifiedDisposal,
  { id: Id }[]
>;

export interface RangeFilterParamConfig
  extends BaseFilterParamConfig<
    FilterType.range,
    FilterRangeState,
    DerivableParamKey<'from' | 'to' | 'metric' | 'currency'>
  > {
  // Emit/seed a metric sub-param, defaulting to this when none is supplied (per-field, e.g. rent vs size).
  defaultMetric?: Metric;
  // Emit a currency sub-param. The default currency is supplied at call time via FilterParamContext.
  currency?: boolean;
}

export type DateFilterParamConfig = BaseFilterParamConfig<
  FilterType.date,
  FilterPeriodState,
  DerivableParamKey<'from' | 'to' | 'ago' | 'ahead'>
>;

export type LookupFilterParamConfig = BaseFilterParamConfig<
  | FilterType.lookupAgency
  | FilterType.lookupAgent
  | FilterType.lookupCompany
  | FilterType.lookupContact
  | FilterType.lookupUser,
  FilterAutocompleteItemState,
  DerivableParamKey<'id' | 'string'>
>;

export type MonthQuarterYearRangeFilterParamConfig = BaseFilterParamConfig<
  FilterType.monthQuarterYearRange,
  FilterMonthQuarterYearRangeState,
  DerivableParamKey<'monthFrom' | 'monthTo' | 'yearFrom' | 'yearTo'>
>;

// Discriminated union of every config, keyed by FilterType — mirrors FiltersTypeConfigs. serialiseFilterState
// / deserialiseFilterState (shared/utils/filter) drive the per-type defaults; a drawer supplies one config map
// and the engine is shared. The `type` discriminant strictly types each entry (e.g. a range config can't take
// `options`, a multiSelect config can't take `defaultMetric`).
export type FilterParamConfigs =
  | MultiSelectFilterParamConfig
  | SingleSelectFilterParamConfig
  | SwitchFilterParamConfig
  | TextFilterParamConfig
  | MultiLookupFilterParamConfig
  | RangeFilterParamConfig
  | DateFilterParamConfig
  | LookupFilterParamConfig
  | MonthQuarterYearRangeFilterParamConfig;

export type FilterParamConfigMap<TState extends Record<number, { value: unknown }>> = {
  [K in keyof TState]: FilterParamConfigs;
};

// Runtime values the engine threads into per-type handlers that aren't field config — e.g. the account's
// default currency. Passed to serialiseFilterState / deserialiseFilterState.
export interface FilterParamContext {
  currencyDefault?: string;
}
